/* eslint-disable jsx-a11y/no-autofocus */
/* eslint-disable jsx-a11y/tabindex-no-positive */
/* eslint-disable @typescript-eslint/unbound-method */
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import type { FC, ReactNode } from "react";

import React, { useCallback, useState } from "react";
import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { Card, Divider } from "antd";
import { z } from "zod";

import type { LoginResult } from "@alaffia-technology-solutions/client-sdk";

import { cn } from "@alaffia-technology-solutions/tailwind-utils";

import type { ChallengeArgs } from "./types";

import { useLog } from "../common/log/useLog";
import { tailwindPreflight } from "../components/app/design/tw-preflight";
import { Icon } from "../components/app/icon";
import { cognitoConfig } from "../components/auth/cognitoConfig";
import { discoverSsoConfig } from "../components/auth/discoverSso";
import {
  discoverIdpForEmail,
  emailSchema,
} from "../components/auth/idpDiscovery";
import { startSsoLogin } from "../components/auth/startSsoLogin";
import {
  discardLoginDurationMetric,
  startLoginDurationMetric,
} from "./loginMetrics";

// className="af-box af-p-4 af-min-w-80 af-w-full af-max-w-md af-transform af-overflow-hidden af-rounded-2xl af-text-left af-align-middle af-shadow-xl af-transition-all af-bg-antd-lt-colorBgContainer dark:af-bg-antd-lt-colorBgContainer"
const clsPanel = cn(
  "af-w-full af-transform af-overflow-hidden af-rounded-2xl af-p-4 af-text-left af-align-middle af-shadow-xl af-transition-all",
  "af-bg-antd-lt-colorBgContainer dark:af-bg-antd-dk-colorBgContainer",
);

// className="af-block af-text-gray-700 af-text-sm font-bold mb-2"
const clsLabel = cn("af-mb-2 af-block af-text-sm af-text-gray-700");

// className="af-shadow af-appearance-none af-border af-border-red-500 af-rounded af-w-full af-py-2 af-px-3 af-text-gray-700 af-mb-3 af-leading-tight focus:af-outline-none focus:af-shadow-outline"
const clsInput = cn(
  "af-mb-3 af-w-full af-appearance-none af-rounded af-border af-px-3 af-py-2 af-leading-tight af-shadow",
  "af-border-antd-lt-colorPrimaryBorder focus:af-border-antd-lt-colorBorderSecondary",
  "af-text-antd-lt-colorText af-placeholder-antd-lt-colorTextPlaceholder",
  "dark:af-border-antd-dk-colorPrimaryBorder dark:focus:af-border-antd-dk-colorBorderSecondary",
  "dark:af-text-antd-dk-colorText dark:af-placeholder-antd-dk-colorTextPlaceholder",
  "disabled:af-border-solid disabled:af-border-gray-200 disabled:af-text-gray-400 disabled:af-shadow-none",
  "placeholder-shown:af-border-antd-lt-colorErrorBorderHover dark:placeholder-shown:af-border-antd-dk-colorErrorBorderHover",
);

const SSO_NOT_AVAILABLE_MESSAGE =
  "SSO is not set up for this email. Please sign in with your username and password above, or contact your admin.";

const SSO_START_FAILED_MESSAGE =
  "Could not start single sign-on. Please try again or contact your admin.";

const SSO_DISCOVERY_FAILED_MESSAGE = "Login failed";

const SSO_DISCOVERY_FLAG_PLACEHOLDER = "${REACT_APP_ENABLE_SSO_DISCOVERY}";

const isRuntimeFlagEnabled = (value?: string) =>
  value?.trim().toLowerCase() === "true";

// Keep this as a runtime read so build optimization does not fold the placeholder comparison.
const getRuntimeFlagValue = () => SSO_DISCOVERY_FLAG_PLACEHOLDER.slice(0);

const isSsoDiscoveryEnabled = () => {
  const runtimeFlag = getRuntimeFlagValue();

  return isRuntimeFlagEnabled(
    runtimeFlag.includes("REACT_APP_ENABLE_SSO_DISCOVERY")
      ? process.env.REACT_APP_ENABLE_SSO_DISCOVERY
      : runtimeFlag,
  );
};

const ssoEmailSchema = z.object({ email: emailSchema });

type SsoEmailFormValues = z.infer<typeof ssoEmailSchema>;

const TermsAndConditions = () => (
  <div className="[&_a]:af-text-antd-lt-colorLink af-flex af-items-center af-whitespace-nowrap af-px-4 af-text-xs">
    <span className="af-text-antd-lt-colorTextSecondary dark:af-text-antd-dk-colorTextSecondary">
      By logging in, you are agreeing to the{" "}
    </span>
    <a
      target="_blank"
      href="https://alaffiahealth.com/privacy-policy/"
      rel="noreferrer"
      className="!af-text-antd-lt-colorLink af-px-2"
    >
      Alaffia Privacy Policy
    </a>
  </div>
);

export const LoginCard: FC<{
  loginResult: LoginResult | null;
  login({ user, pass }: { user: string; pass: string }): void;
  challenge(args: ChallengeArgs): void;
  forgotPassword(user: string): void;
  isOpen: boolean;
  lastError?: ReactNode;
  isForgotPassword: boolean;
  setIsForgotPassword: (value: boolean) => void;
}> = ({
  loginResult,
  login,
  challenge,
  forgotPassword,
  isOpen,
  lastError,
  isForgotPassword,
  setIsForgotPassword,
}) => {
  const log = useLog("LoginCard");

  const [isSsoEmailStep, setIsSsoEmailStep] = useState(false);
  const [isRedirectingToSso, setIsRedirectingToSso] = useState(false);

  const {
    register: registerSsoField,
    handleSubmit: handleSsoFormSubmit,
    setError: setSsoError,
    reset: resetSsoForm,
    formState: { errors: ssoErrors, isSubmitting: isDiscovering },
  } = useForm<SsoEmailFormValues>({
    resolver: zodResolver(ssoEmailSchema),
    defaultValues: { email: "" },
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const ssoEmailError = ssoErrors.email?.message;
  const canSubmitSsoEmail = !isDiscovering && !isRedirectingToSso;

  const submitSsoEmail = useCallback(
    async ({ email }: SsoEmailFormValues) => {
      startLoginDurationMetric("sso");

      if (isSsoDiscoveryEnabled()) {
        const discovery = await discoverSsoConfig(email);
        if (discovery.status !== "found") {
          discardLoginDurationMetric();
          if (discovery.status === "error") {
            log.error("SSO discovery failed", { message: discovery.message });
          } else {
            log.debug("No SSO config discovered for email");
          }
          setSsoError("email", { message: SSO_DISCOVERY_FAILED_MESSAGE });
          return;
        }

        setIsRedirectingToSso(true);
        try {
          await startSsoLogin({
            domain: discovery.config.cognitoDomain,
            clientId: discovery.config.cognitoClientId,
            identityProvider: discovery.config.cognitoIdentityProvider,
            loginHint: email,
          });
        } catch (err) {
          discardLoginDurationMetric();
          log.error("SSO login failed to start", err);
          setIsRedirectingToSso(false);
          setSsoError("email", { message: SSO_DISCOVERY_FAILED_MESSAGE });
        }
        return;
      }

      const result = discoverIdpForEmail(email);
      if (result.status !== "found") {
        discardLoginDurationMetric();
        log.debug("No IdP mapped for SSO email domain", {
          domain: result.domain,
        });
        setSsoError("email", { message: SSO_NOT_AVAILABLE_MESSAGE });
        return;
      }

      setIsRedirectingToSso(true);
      try {
        await startSsoLogin({
          domain: cognitoConfig.domain,
          clientId: cognitoConfig.clientId,
          identityProvider: result.identityProvider,
          loginHint: email,
        });
      } catch (err) {
        discardLoginDurationMetric();
        log.error("SSO login failed to start", err);
        setIsRedirectingToSso(false);
        setSsoError("email", { message: SSO_START_FAILED_MESSAGE });
      }
    },
    [log, setSsoError],
  );

  const signinMessage = isForgotPassword
    ? "Reset Password"
    : !loginResult
      ? "Sign in"
      : loginResult?.predicates?.isConfigureMfa(loginResult)
        ? "Save MFA"
        : loginResult?.predicates?.isLoginChallenge(loginResult)
          ? "Continue"
          : loginResult?.predicates?.isForceChangePassword(loginResult)
            ? "Change Password"
            : "Sign in";

  const handleLogin = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const data = new FormData(event.currentTarget);
      if (isForgotPassword) {
        const user = data.get("username") as string;
        if (!user) {
          throw new Error("User name is required");
        }
        forgotPassword(user);
      } else if (
        loginResult?.predicates.isLoginChallenge(loginResult) ||
        loginResult?.predicates.isConfigureMfa(loginResult)
      ) {
        challenge({
          code: data.get("code") as string,
        });
      } else if (loginResult?.predicates.isForceChangePassword(loginResult)) {
        challenge({
          newPassword: data.get("newPassword") as string,
          confirmPassword: data.get("confirmPassword") as string,
        });
      } else {
        login({
          user: data.get("username") as string,
          pass: data.get("password") as string,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [login, challenge, loginResult, isForgotPassword],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div className={tailwindPreflight}>
      <div className="af-fixed af-inset-0 af-z-10">
        <div
          className="af-fixed af-inset-0 af-bg-black/25"
          role="button"
          tabIndex={0}
        />
        <div className="af-fixed af-inset-0 af-overflow-y-auto">
          <div className="af-mx-auto af-flex af-min-h-full af-max-w-md af-items-center af-justify-center af-p-4 af-text-center">
            <div className={cn("af-min-w-[375px] af-max-w-[50vw]", clsPanel)}>
              <Card className="af-w-full">
                <div className="af-text-lg af-font-medium af-leading-6 af-text-gray-600">
                  <div className="af-flex af-h-16 af-flex-col af-items-center af-gap-2">
                    <Icon
                      className={cn(
                        "af-flex af-h-full af-items-center",
                        "[&_img]:af-h-auto [&_img]:af-w-[30px] [&_img]:af-flex-none [&_img]:af-animate-fade-in",
                      )}
                      data-testid="header-icon"
                    ></Icon>
                    <div className="af-w-full af-place-self-center af-text-center">
                      Login to Alaffia
                    </div>
                    <div className="af-w-16"></div>
                  </div>
                </div>
                <div className="af-mt-8 af-text-sm">
                  <div className="w-full max-w-xs">
                    <form
                      onSubmit={handleLogin}
                      className={isSsoEmailStep ? "af-hidden" : undefined}
                    >
                      {isForgotPassword ? (
                        <>
                          <div className="_mb-6">
                            <label className={clsLabel} htmlFor="username">
                              Enter the email address or username registered
                              with Alaffia and click continue to send a reset
                              code to your email
                            </label>
                            <input
                              className={clsInput}
                              name="username"
                              id="username"
                              placeholder="Enter email address or username"
                              defaultValue={""}
                              disabled={false}
                              autoFocus
                              tabIndex={1}
                              type="text"
                              autoComplete="username"
                              required
                              title={""}
                            />
                          </div>
                          <div className="_mb-4 _flex _justify-between">
                            &nbsp;
                          </div>
                        </>
                      ) : loginResult?.predicates?.isConfigureMfa(
                          loginResult,
                        ) ? (
                        <div className="af-mb-6">
                          <div style={{ paddingTop: "10px" }}>
                            1. Install a compatible app on your mobile device or
                            computer
                          </div>
                          <Divider />
                          <div style={{ paddingTop: "10px" }}>
                            2. Use your virtual MFA app and your devices camera
                            to scan the QR code
                          </div>
                          <div style={{ paddingTop: "10px" }}>
                            <img alt="" src={loginResult?.totpQrCode ?? ""} />
                          </div>
                          <div style={{ paddingTop: "5px" }}>
                            Alternatively you can type in the secret key:{" "}
                            {loginResult?.totpSecret ?? ""}
                          </div>
                          <Divider />
                          <div style={{ paddingTop: "10px" }}>
                            3. Type the next MFA code below from your virtual
                            MFA app
                          </div>
                          <div className="af-mb-6">
                            <label className={clsLabel} htmlFor="password">
                              MFA Code{" "}
                              {loginResult.challengeKind === "EMAIL_OTP"
                                ? `( sent to ${loginResult.authParams.codeDeliveryDestination} )`
                                : ""}
                            </label>
                            <input
                              className={clsInput}
                              name="code"
                              id="code"
                              type="password"
                              placeholder="Enter MFA code"
                              tabIndex={1}
                              autoFocus
                              autoComplete={
                                loginResult.challengeKind ===
                                "SOFTWARE_TOKEN_MFA"
                                  ? "one-time-code"
                                  : undefined
                              }
                              required
                            />
                          </div>
                        </div>
                      ) : loginResult?.predicates?.isLoginChallenge(
                          loginResult,
                        ) ? (
                        <div className="af-mb-6">
                          <label className={clsLabel} htmlFor="password">
                            MFA Code{" "}
                            {loginResult.challengeKind === "EMAIL_OTP"
                              ? `( sent to ${loginResult.authParams.codeDeliveryDestination} )`
                              : ""}
                          </label>
                          <input
                            className={clsInput}
                            name="code"
                            id="code"
                            placeholder="Enter MFA Code"
                            type="password"
                            tabIndex={1}
                            autoFocus
                            autoComplete={
                              loginResult.challengeKind === "SOFTWARE_TOKEN_MFA"
                                ? "one-time-code"
                                : undefined
                            }
                            required
                          />
                        </div>
                      ) : loginResult?.predicates?.isForceChangePassword(
                          loginResult,
                        ) ? (
                        <>
                          <div className="af-mb-4">
                            <label className={clsLabel} htmlFor="username">
                              Username
                            </label>
                            <input
                              className={clsInput}
                              name="username"
                              id="username"
                              placeholder="Enter user name"
                              defaultValue={
                                loginResult?.authParams.username ?? ""
                              }
                              disabled={!!loginResult?.authParams.username}
                              autoFocus
                              tabIndex={1}
                              type="text"
                              autoComplete="username"
                              required
                              title={
                                "username: " +
                                (loginResult?.authParams.username ?? "")
                              }
                            />
                          </div>
                          <div className="af-mb-6">
                            <label className={clsLabel} htmlFor="newPassword">
                              New Password
                            </label>
                            <input
                              className={clsInput}
                              name="newPassword"
                              id="newPassword"
                              placeholder="Enter new password"
                              type="password"
                              autoComplete="new-password"
                              tabIndex={2}
                              required
                            />
                            <ul className="af-grid af-list-disc af-grid-cols-2 af-gap-x-4 af-gap-y-1 af-pl-4 af-text-sm af-text-muted-foreground">
                              <li>Min. 10 characters</li>
                              <li>1 special character</li>
                              <li>1 number</li>
                              <li>1 upper & lower case</li>
                            </ul>
                          </div>

                          <div className="af-mb-6">
                            <label
                              className={clsLabel}
                              htmlFor="confirmPassword"
                            >
                              Confirm New Password
                            </label>
                            <input
                              className={clsInput}
                              name="confirmPassword"
                              id="confirmPassword"
                              placeholder="Confirm new password"
                              type="password"
                              autoComplete="new-password"
                              tabIndex={2}
                              required
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="af-mb-4">
                            <label className={clsLabel} htmlFor="username">
                              Username
                            </label>
                            <input
                              className={clsInput}
                              name="username"
                              id="username"
                              placeholder="Enter user name"
                              autoFocus
                              tabIndex={1}
                              type="text"
                              autoComplete="username"
                              required
                            />
                          </div>
                          <div className="af-mb-6">
                            <label className={clsLabel} htmlFor="password">
                              Password
                            </label>
                            <input
                              className={clsInput}
                              name="password"
                              id="password"
                              placeholder="Enter password"
                              type="password"
                              autoComplete="current-password"
                              tabIndex={2}
                              required
                            />
                          </div>
                        </>
                      )}
                      <div className="af-flex af-justify-between">
                        <div className=""></div>
                        <button
                          tabIndex={3}
                          type="submit"
                          className="af-ml-auto af-inline-flex af-justify-center af-rounded-md af-border af-border-transparent af-bg-blue-100 af-px-4 af-py-2 af-text-sm af-font-medium af-text-blue-900 hover:af-bg-blue-200 focus:af-outline-none focus-visible:af-ring-2 focus-visible:af-ring-blue-500 focus-visible:af-ring-offset-2"
                        >
                          {signinMessage}
                        </button>
                      </div>
                    </form>
                    {!isForgotPassword && !loginResult && (
                      <div className="af-mt-4 af-flex af-flex-col af-gap-2">
                        <div
                          className={cn(
                            "af-flex af-items-center af-gap-2",
                            isSsoEmailStep && "af-hidden",
                          )}
                        >
                          <div className="af-h-px af-flex-1 af-bg-gray-300"></div>
                          <span className="af-text-xs af-text-gray-500">
                            or
                          </span>
                          <div className="af-h-px af-flex-1 af-bg-gray-300"></div>
                        </div>
                        {isSsoEmailStep ? (
                          <form
                            onSubmit={(event) => {
                              void handleSsoFormSubmit(submitSsoEmail)(event);
                            }}
                            noValidate
                          >
                            <label className={clsLabel} htmlFor="ssoEmail">
                              Work email
                            </label>
                            <input
                              {...registerSsoField("email")}
                              className={clsInput}
                              id="ssoEmail"
                              type="email"
                              placeholder="Enter your work email"
                              autoFocus
                              tabIndex={4}
                              autoComplete="email"
                              aria-invalid={!!ssoEmailError}
                              aria-describedby={
                                ssoEmailError ? "ssoEmailError" : undefined
                              }
                            />
                            {ssoEmailError && (
                              <div
                                id="ssoEmailError"
                                role="alert"
                                data-testid="sso-email-error"
                                className="af-text-antd-lt-colorError dark:af-text-antd-dk-colorError af-mb-3 af-text-xs"
                              >
                                {ssoEmailError}
                              </div>
                            )}
                            <button
                              type="submit"
                              tabIndex={4}
                              disabled={!canSubmitSsoEmail}
                              className="af-inline-flex af-w-full af-justify-center af-rounded-md af-border af-border-gray-300 af-bg-white af-px-4 af-py-2 af-text-sm af-font-medium af-text-gray-700 hover:af-bg-gray-50 focus:af-outline-none focus-visible:af-ring-2 focus-visible:af-ring-blue-500 focus-visible:af-ring-offset-2 disabled:af-cursor-not-allowed disabled:af-border-gray-200 disabled:af-bg-gray-50 disabled:af-text-gray-400 disabled:hover:af-bg-gray-50"
                            >
                              {isDiscovering || isRedirectingToSso
                                ? "Checking..."
                                : "Continue"}
                            </button>
                          </form>
                        ) : (
                          <button
                            type="button"
                            tabIndex={4}
                            onClick={() => setIsSsoEmailStep(true)}
                            className="af-inline-flex af-w-full af-justify-center af-rounded-md af-border af-border-gray-300 af-bg-white af-px-4 af-py-2 af-text-sm af-font-medium af-text-gray-700 hover:af-bg-gray-50 focus:af-outline-none focus-visible:af-ring-2 focus-visible:af-ring-blue-500 focus-visible:af-ring-offset-2"
                          >
                            Continue with SSO
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={cn(
                    "af-text-antd-lt-colorError dark:af-text-antd-dk-colorError af-p-2 af-text-xs",
                    isSsoEmailStep && "af-hidden",
                  )}
                >
                  {lastError}
                </div>
                <div
                  className={cn(
                    "af-flex af-justify-center",
                    isSsoEmailStep && "af-mt-4",
                  )}
                >
                  {isForgotPassword || isSsoEmailStep ? (
                    <button
                      tabIndex={4}
                      onClick={() => {
                        setIsForgotPassword(false);
                        setIsSsoEmailStep(false);
                        resetSsoForm();
                      }}
                      className="af-text-antd-lt-colorLink af-cursor-pointer af-border-none af-bg-inherit af-p-0 af-text-xs"
                    >
                      Return to login screen
                    </button>
                  ) : (
                    <button
                      onClick={() => setIsForgotPassword(true)}
                      tabIndex={4}
                      className="af-text-antd-lt-colorLink af-cursor-pointer af-border-none af-bg-inherit af-p-0 af-text-xs af-underline"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
              </Card>
              <TermsAndConditions />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
